/**
 * Referral client — two-sided give-get (attribution + read-only stats only;
 * the 150/150 reward fires server-side from the Stripe webhook, never here).
 *
 *   captureRefFromUrl()  — landing: read ?ref=, store + log click (funnel top)
 *   attributeReferral()  — app entry: tell worker "this signup came from X"
 *   getReferralInfo()    — Settings: inviter's link + invited/completed counts
 *
 * Attribution fires once per stored ref (idempotent server-side too); the
 * stored ref is cleared after attribution so re-logins don't re-fire.
 */
import { API_URL } from '@/env'
import { buildAuthHeaders } from './auth-client'

const REF_KEY = 'ref_code'

export interface ReferralInfo {
  code: string
  link: string
  invited: number
  completed: number
  unread_rewards: number
}

/** Landing mount: capture ?ref=, persist, log the click for funnel analytics. */
export async function captureRefFromUrl(): Promise<void> {
  const params = new URLSearchParams(window.location.search)
  const ref = params.get('ref')
  if (!ref) return
  // Don't overwrite a ref already stored (first-touch wins).
  if (localStorage.getItem(REF_KEY)) return
  localStorage.setItem(REF_KEY, ref)

  try {
    await fetch(`${API_URL}/api/referral/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
      body: JSON.stringify({ refCode: ref }),
    })
  } catch {
    // Click log is best-effort — don't block the landing page on it.
  }
}

/**
 * App entry: attribute this user to the stored referrer, then clear it.
 * Safe to call on every app load — server dedups (one attribution per user).
 */
export async function attributeReferral(): Promise<void> {
  const ref = localStorage.getItem(REF_KEY)
  if (!ref) return
  try {
    const res = await fetch(`${API_URL}/api/referral/attribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
      body: JSON.stringify({ refCode: ref }),
    })
    if (res.ok) {
      // Attribution recorded (or no-op) — clear so we never re-send.
      localStorage.removeItem(REF_KEY)
    }
  } catch {
    // Network error — keep the ref so a later load retries.
  }
}

export type AttributionReason = 'unknown' | 'self' | 'exists'
export interface AttributionOutcome {
  attributed: boolean
  reason?: AttributionReason
}

/**
 * Manual code entry — the rescue path when localStorage was lost between
 * landing and signup (cross-device, Safari ITP eviction, cleared storage).
 * User types a friend's code in the top-up card; the server attributes them
 * and both sides get 150 energy on this user's next paid top-up (give-get,
 * ADR 0005). Returns null only on network failure (caller may retry).
 */
export async function attributeReferralWithCode(
  code: string
): Promise<AttributionOutcome | null> {
  const trimmed = code.trim().toUpperCase()
  if (!trimmed) return { attributed: false, reason: 'unknown' }
  try {
    const res = await fetch(`${API_URL}/api/referral/attribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
      body: JSON.stringify({ refCode: trimmed }),
    })
    if (!res.ok) return null
    // Strip the server envelope — the client contract is {attributed, reason} only.
    const body = (await res.json()) as AttributionOutcome
    return { attributed: body.attributed, reason: body.reason }
  } catch {
    return null
  }
}

export async function getReferralInfo(): Promise<ReferralInfo | null> {
  try {
    const res = await fetch(`${API_URL}/api/referral/info`, {
      headers: buildAuthHeaders(),
    })
    if (!res.ok) return null
    return (await res.json()) as ReferralInfo
  } catch {
    return null
  }
}

/** Inviter dismissed the reward banner — stamp the watermark server-side. */
export async function acknowledgeRewards(): Promise<void> {
  try {
    await fetch(`${API_URL}/api/referral/acknowledge`, {
      method: 'POST',
      headers: buildAuthHeaders(),
    })
  } catch {
    // best-effort — banner re-showing is harmless
  }
}
